import { Book, Chapter } from '../types/Book';
import { ChapterProgress } from '../types/ChapterNavigation';

interface ChapterProgressData {
  completed: boolean;
  audioPosition?: number;
  lastVisited?: number;
  completedAt?: number;
}

interface BookProgressData {
  [chapterFile: string]: ChapterProgressData;
}

export class ChapterProgressService {
  private static instance: ChapterProgressService;
  
  static getInstance(): ChapterProgressService {
    if (!ChapterProgressService.instance) {
      ChapterProgressService.instance = new ChapterProgressService();
    }
    return ChapterProgressService.instance;
  }

  private getStorageKey(bookId: string): string {
    return `voyager_progress_${bookId}`;
  }

  private getBookmarksKey(bookId: string): string {
    return `voyager_bookmarks_${bookId}`;
  }

  // Get all progress data for a book
  getBookProgress(bookId: string): BookProgressData {
    try {
      const stored = localStorage.getItem(this.getStorageKey(bookId));
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Failed to load chapter progress:', error);
      return {};
    }
  }

  // Get progress for a specific chapter
  getChapterProgress(bookId: string, chapterFile: string): ChapterProgressData | null {
    const bookProgress = this.getBookProgress(bookId);
    return bookProgress[chapterFile] || null;
  }

  // Update progress for a specific chapter
  updateChapterProgress(
    bookId: string, 
    chapterFile: string, 
    data: Partial<ChapterProgressData>
  ): void {
    try {
      const bookProgress = this.getBookProgress(bookId);
      
      bookProgress[chapterFile] = {
        ...bookProgress[chapterFile],
        ...data,
        lastVisited: Date.now()
      };

      localStorage.setItem(this.getStorageKey(bookId), JSON.stringify(bookProgress));
    } catch (error) {
      console.warn('Failed to save chapter progress:', error);
    }
  }

  // Mark chapter as completed
  markChapterCompleted(bookId: string, chapterFile: string): void {
    this.updateChapterProgress(bookId, chapterFile, {
      completed: true,
      completedAt: Date.now()
    });
  }

  // Update audio position
  updateAudioPosition(bookId: string, chapterFile: string, position: number): void {
    this.updateChapterProgress(bookId, chapterFile, {
      audioPosition: position
    });
  }

  // Check if chapter is unlocked based on story progression
  isChapterUnlocked(
    book: Book, 
    chapterFile: string, 
    chapterHistory: any[]
  ): boolean {
    // First chapter is always unlocked
    const chapterIndex = book.chapters.findIndex(ch => ch.file === chapterFile);
    if (chapterIndex === 0) return true;

    // If chapter has been visited before, it's unlocked
    if (chapterHistory.some(h => h.chapterFile === chapterFile)) {
      return true;
    }

    // For linear stories, unlock chapters sequentially
    if (!book.paths || book.paths.length <= 1) {
      const bookProgress = this.getBookProgress(book.id);
      
      // Check if previous chapter has been completed or visited
      for (let i = 0; i < chapterIndex; i++) {
        const prevChapter = book.chapters[i];
        const prevProgress = bookProgress[prevChapter.file];
        const wasVisited = chapterHistory.some(h => h.chapterFile === prevChapter.file);
        
        if (prevProgress?.completed || wasVisited) {
          return true;
        }
      }
      return false;
    }

    // For branching stories, unlock based on story paths
    return this.isChapterUnlockedInBranchingStory(book, chapterFile, chapterHistory);
  }

  private isChapterUnlockedInBranchingStory(
    book: Book, 
    chapterFile: string,
    chapterHistory: any[]
  ): boolean {
    const chapter = book.chapters.find(ch => ch.file === chapterFile);
    if (!chapter) return false;

    // Get the chapter number for basic progression logic
    const chapterNumber = parseInt(chapterFile.replace('.md', ''));
    
    // First few chapters are usually unlocked
    if (chapterNumber <= 2) return true;

    // Check if any chapter that can lead to this one has been visited
    const canReachChapter = book.chapters.some(ch => {
      if (ch.paths.includes(chapterFile.replace('.md', ''))) {
        return chapterHistory.some(h => h.chapterFile === ch.file);
      }
      return false;
    });

    return canReachChapter;
  }

  // Get bookmarked chapters
  getBookmarks(bookId: string): string[] {
    try {
      const stored = localStorage.getItem(this.getBookmarksKey(bookId));
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load bookmarks:', error);
      return [];
    }
  }

  // Toggle bookmark for a chapter
  toggleBookmark(bookId: string, chapterFile: string): boolean {
    try {
      const bookmarks = this.getBookmarks(bookId);
      const index = bookmarks.indexOf(chapterFile);
      
      if (index > -1) {
        bookmarks.splice(index, 1);
      } else {
        bookmarks.push(chapterFile);
      }
      
      localStorage.setItem(this.getBookmarksKey(bookId), JSON.stringify(bookmarks));
      return index === -1; // Return true if bookmarked, false if unbookmarked
    } catch (error) {
      console.warn('Failed to toggle bookmark:', error);
      return false;
    }
  }

  // Build chapter progress array for navigation
  buildChapterProgressArray(
    book: Book,
    currentChapterFile: string,
    chapterHistory: any[]
  ): ChapterProgress[] {
    const bookProgress = this.getBookProgress(book.id);
    
    return book.chapters.map(chapter => {
      const progress = bookProgress[chapter.file];
      const isCurrentlyPlaying = chapter.file === currentChapterFile;
      const isUnlocked = this.isChapterUnlocked(book, chapter.file, chapterHistory);
      
      return {
        chapterFile: chapter.file,
        isCompleted: progress?.completed || false,
        isCurrentlyPlaying,
        audioPosition: progress?.audioPosition,
        lastVisited: progress?.lastVisited,
        isUnlocked,
        pathTaken: this.getChapterPath(book, chapter)
      };
    });
  }

  private getChapterPath(book: Book, chapter: Chapter): string {
    // Check if this is a branching story
    if (!book.paths || book.paths.length <= 1) {
      return 'main';
    }

    // Use path_name if available
    if (chapter.path_name) {
      return chapter.path_name;
    }

    // Try to determine path from chapter number and story structure
    const chapterNumber = parseInt(chapter.file.replace('.md', ''));
    
    // Basic path detection for Memory Thief structure
    if (chapterNumber === 2) return 'Detective';
    if (chapterNumber === 18) return 'Conspirator';
    if (chapterNumber === 34) return 'Scientist';
    
    return 'main';
  }

  // Get recently visited chapters
  getRecentChapters(bookId: string, limit: number = 5): string[] {
    const bookProgress = this.getBookProgress(bookId);
    
    return Object.entries(bookProgress)
      .filter(([_, progress]) => progress.lastVisited)
      .sort(([_, a], [__, b]) => (b.lastVisited || 0) - (a.lastVisited || 0))
      .slice(0, limit)
      .map(([chapterFile]) => chapterFile);
  }

  // Clear all progress for a book (useful for testing or reset)
  clearBookProgress(bookId: string): void {
    try {
      localStorage.removeItem(this.getStorageKey(bookId));
      localStorage.removeItem(this.getBookmarksKey(bookId));
    } catch (error) {
      console.warn('Failed to clear book progress:', error);
    }
  }
}

export const chapterProgressService = ChapterProgressService.getInstance();